import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import { Divider, Grid, List, ListItem, ListItemText, Paper, TextField, Typography } from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import { lineItemValidationSchema } from '../../validations/LineItemValidation';
import { lineItemCalculation } from '../../utils/pricingEngineForDocuments';
import UOMSelect from './UOMSelect';
import ItemCodeSelect from './ItemCodeSelect';
import TaxTypeSelect from './TaxTypeSelect';
import NumberOrPercentageInput from '../NumberOrPercentageInput';
import { currencyFormatter } from '../../utils/currencyFormat';
import { TAX_TYPES } from '../../utils/taxTypes';
import TaxExemptionSection from '../../resources/Invoices/components/TaxExemptionSection';
import ProductSelect from './ProductSelect';
import { useSelector } from 'react-redux';

const defaultState = {
  item_code: null,
  unit_of_measure: null,
  description: '',
  discount_amount: 0,
  charge_amount: 0,
  tax_amount: 0,
  quantity: 1,
  unit_rate: 1,
  discounts: [
    {
      value: 0,
      amount: 0,
      type: 'percentage',
    },
  ],
  charges: [
    {
      value: 0,
      amount: 0,
      type: 'percentage',
    },
  ],
  taxes: [
    {
      amount: 0,
      type: 'percentage',
      value: 0,
      category: null,
    },
  ],
  tax_exemption: {
    amount: 0,
    tax: 0,
    reason: '',
  },
};

const LineItemForm = ({ submitForm, handleClose, initialData = defaultState }) => {
  const itemCodes = useSelector((state) => state.itemCodes.itemCodes);
  const uomCodes = useSelector((state) => state.uomCodes.uomCodes);
  const taxTypes = useSelector((state) => state.taxTypes.taxTypes);

  const itemCodesOptions = itemCodes.map((item) => ({ key: item.Code, value: item.Description }));
  const uomCodesOptions = uomCodes.map((item) => ({ key: item.Code, value: item.Name }));
  const taxTypesOptions = taxTypes.map((item) => ({ key: item.Code, value: item.Description }));

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    resetField,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(lineItemValidationSchema),
    defaultValues: {
      item_code: initialData?.item_code || null,
      unit_of_measure: initialData?.unit_of_measure || null,
      description: initialData?.description || '',
      discount_amount: initialData?.discount_amount || 0,
      charge_amount: initialData?.charge_amount || 0,
      tax_amount: initialData?.tax_amount || 0,
      quantity: initialData?.quantity || 1,
      unit_rate: initialData?.unit_rate || 1,
      discounts: initialData?.discounts || [{ value: 0, amount: 0, type: 'percentage' }],
      charges: initialData?.charges || [{ value: 0, amount: 0, type: 'percentage' }],
      taxes: initialData?.taxes || [{ value: 0, amount: 0, type: 'percentage', category: null }],
      tax_exemption: initialData?.tax_exemption || { amount: 0, tax: 0, reason: '' },
    },
  });

  const {
    fields: discountFields,
    append: appendDiscount,
    remove: removeDiscount,
  } = useFieldArray({
    control: control,
    name: 'discounts',
  });

  const {
    fields: chargeFields,
    append: appendCharge,
    remove: removeCharge,
  } = useFieldArray({
    control: control,
    name: 'charges',
  });

  const {
    fields: taxFields,
    append: appendTax,
    remove: removeTax,
  } = useFieldArray({
    control: control,
    name: 'taxes',
  });

  const lineItemsData = watch();
  delete lineItemsData.product;
  let pricingEngine = lineItemCalculation(lineItemsData);

  const onProductSelect = (product) => {
    setValue('description', product?.description || '');
    setValue('unit_rate', product?.unit_rate || 1);
    setValue('item_code', itemCodesOptions?.find((type) => type.key === product?.item_code) || null);
    setValue('quantity', product?.quantity || 1);
    setValue('unit_of_measure', uomCodesOptions?.find((type) => type.key === product?.unit_of_measure) || null);
    setValue(
      'taxes',
      (product?.taxes?.length ? product.taxes : [{ type: 'percentage', value: 0, amount: 0, category: null }]).map(
        ({ category, ...rest }) => ({
          ...rest,
          category: taxTypesOptions.find((type) => type.key === category) || null,
        }),
      ),
    );

    setValue('charges', [{ amount: 0, type: 'percentage', value: 0, reason: '' }]);
    setValue('discounts', [{ amount: 0, type: 'percentage', value: 0, reason: '' }]);
  };

  const onDescriptionChange = (description) => {
    setValue('description', description || '');
  };

  const handleSave = (data) => {
    const itemData = {
      ...data,
      item_code: data.item_code.key,
      unit_of_measure: data.unit_of_measure.key,
      taxes: data.taxes.map(({ category, ...rest }) => ({ category: category.key, ...rest })),
    };
    delete itemData.product;
    const lineItem = lineItemCalculation(itemData);
    submitForm(lineItem);
  };

  const handleSinglePopupFormSubmit = (data) => {
    handleSave(data);
    handleClose();
  };

  const handleMultiplePopupFormSubmit = (data) => {
    handleSave(data);
    reset(defaultState);
    setValue('product', {});
  };

  useEffect(() => {
    setValue('product', initialData);
  }, [setValue, initialData]);

  return (
    <>
      <Paper elevation={0} sx={{ p: '1rem', overflow: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Item Description */}
            <Grid size={{ xs: 12 }}>
              <ProductSelect
                label="Description"
                name="product"
                control={control}
                errors={errors}
                selectedOption={initialData}
                onProductSelect={onProductSelect}
                onDescriptionChange={onDescriptionChange}
              />
            </Grid>

            {/* Classification Code */}
            <Grid size={{ xs: 12 }}>
              <ItemCodeSelect control={control} errors={errors} name="item_code" />
            </Grid>

            {/* Unit of Measure */}
            <Grid size={{ xs: 4 }}>
              <UOMSelect control={control} errors={errors} name="unit_of_measure" />
            </Grid>

            {/* Quantity */}
            <Grid size={{ xs: 4 }}>
              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="quantity"
                    label="Quantity"
                    variant="outlined"
                    fullWidth
                    name="quantity"
                    type="number"
                    error={!!errors.quantity}
                    helperText={errors.quantity?.message}
                    onBlur={() => {
                      if (field.value) {
                        const trimmedValue = parseFloat(field.value);
                        field.onChange(trimmedValue);
                      }
                    }}
                    onChange={(event) => {
                      const inputValue = event.target.value;
                      if (/^\d*\.?\d{0,8}$/.test(inputValue) || inputValue === '') {
                        field.onChange(inputValue);
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Rate */}
            <Grid size={{ xs: 4 }}>
              <Controller
                name="unit_rate"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="unit_rate"
                    label="Unit Price"
                    variant="outlined"
                    fullWidth
                    type="number"
                    name="unit_rate"
                    {...field}
                    error={!!errors.unit_rate}
                    helperText={errors.unit_rate?.message}
                    onBlur={() => {
                      if (field.value) {
                        const trimmedValue = parseFloat(field.value);
                        field.onChange(trimmedValue);
                      }
                    }}
                    onChange={(event) => {
                      const inputValue = event.target.value;
                      if (/^\d*\.?\d{0,8}$/.test(inputValue) || inputValue === '') {
                        field.onChange(inputValue);
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
            Discounts & Charges
            <Divider></Divider>
          </Typography>

          {discountFields.map((charge, discountIndex) => (
            <Grid key={charge.id} container spacing={2} rowspacing={2} sx={{ pb: 3 }}>
              <Grid size={{ xs: 6 }}>
                <NumberOrPercentageInput
                  value={`discounts.${discountIndex}.value`}
                  type={`discounts.${discountIndex}.type`}
                  id={'discounts-value'}
                  label={'Discounts Value'}
                  control={control}
                  errors={!!errors.discounts?.[discountIndex]}
                  helperText={errors.discounts?.[discountIndex]?.value?.message}
                  decimal={8}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Controller
                  name={`discounts.${discountIndex}.reason`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="charge-reason"
                      fullWidth
                      name="reason"
                      label="Discount Reason"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>
          ))}
          {chargeFields.map((charge, chargeIndex) => (
            <Grid key={charge.id} container spacing={2} rowspacing={2}>
              <Grid size={{ xs: 6 }}>
                <NumberOrPercentageInput
                  value={`charges.${chargeIndex}.value`}
                  type={`charges.${chargeIndex}.type`}
                  id={'charges-value'}
                  label={'Charges Value'}
                  control={control}
                  error={!!errors.charges?.[chargeIndex]?.value}
                  helperText={errors.charges?.[chargeIndex]?.value?.message}
                  decimal={8}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Controller
                  name={`charges.${chargeIndex}.reason`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="charge-reason"
                      fullWidth
                      name="reason"
                      label="Charge Reason"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>
          ))}

          <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
            Taxes
            <Divider></Divider>
          </Typography>

          {taxFields.map((charge, taxIndex) => (
            <Grid key={charge.id} container spacing={2} rowspacing={2}>
              <Grid size={{ xs: 6 }}>
                <TaxTypeSelect
                  control={control}
                  errors={errors}
                  name={`taxes.${taxIndex}.category`}
                  setValue={setValue}
                  index={taxIndex}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <NumberOrPercentageInput
                  value={`taxes.${taxIndex}.value`}
                  type={`taxes.${taxIndex}.type`}
                  id={'taxes-value'}
                  label={'Taxes Value'}
                  control={control}
                  setValue={setValue}
                  disableType={watch(`taxes.${taxIndex}.category.key`) === TAX_TYPES.TourismTax.Code}
                  disabled={watch(`taxes.${taxIndex}.category.key`) === TAX_TYPES.NotApplicable.Code}
                  error={!!errors.taxes?.[taxIndex]?.value}
                  helperText={errors.taxes?.[taxIndex]?.value?.message}
                />
              </Grid>
            </Grid>
          ))}

          <TaxExemptionSection
            control={control}
            errors={errors}
            pricingEngine={pricingEngine}
            resetField={resetField}
            getValues={getValues}
          />
        </Box>
        <Divider sx={{ py: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <List sx={{ width: '50%' }}>
            <ListItem>
              <ListItemText primary="Base amount" />
              <Typography variant="body1">{currencyFormatter().format(pricingEngine.base_amount)}</Typography>
            </ListItem>
            <ListItem>
              <ListItemText id="discount" primary="Discount" />
              <Typography variant="body1" color="error">
                -{currencyFormatter().format(pricingEngine.discount_amount)}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText id="charge" primary="Charge" />
              <Typography variant="body1">{currencyFormatter().format(pricingEngine.charge_amount)}</Typography>
            </ListItem>
            <ListItem>
              <ListItemText primary="Total before tax" />
              <Typography variant="body1">{currencyFormatter().format(pricingEngine.net_amount)}</Typography>
            </ListItem>
            <ListItem>
              <ListItemText primary="Tax" />
              <Typography variant="body1">{currencyFormatter().format(pricingEngine.tax_amount)}</Typography>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary="Sub Total" />
              <Typography variant="h6">{currencyFormatter().format(pricingEngine.total_amount)}</Typography>
            </ListItem>
          </List>
        </Box>
      </Paper>
      <Divider />
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button
          id="save-and-add-new-item"
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleMultiplePopupFormSubmit)} // Add the item when clicked
        >
          Save & Add new item
        </Button>
        <Button
          id="save-item"
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleSinglePopupFormSubmit)} // Add the item when clicked
        >
          Save Item
        </Button>
      </Box>
    </>
  );
};

export default LineItemForm;
